'use client';

/**
 * Moteur vocal global « LE SITE PARLE » — inspiré de ndiamsyl-peye.com
 * 1) Primaire : Google Translate TTS (translate_tts client=gtx) via élément Audio
 * 2) Fallback : Web Speech API (SpeechSynthesisUtterance)
 * 3) File d'attente anti-chevauchement avec pauses configurables + barre de statut
 */

export interface SpeakItem {
  text: string;
  lang?: 'ar' | 'fr' | 'en';
  rate?: number;
  pause?: number; // pause après cet item (ms)
}

type Listener = (state: { speaking: boolean; current: string }) => void;

const listeners = new Set<Listener>();
let queue: (SpeakItem & { resolved: boolean })[] = [];
let processing = false;
let stopped = false;
let sfxAudio: HTMLAudioElement | null = null;
let statusEl: HTMLDivElement | null = null;

const LANG_MAP = { ar: 'ar-SA', fr: 'fr-FR', en: 'en-US' };

function getAudio(): HTMLAudioElement {
  if (!sfxAudio) sfxAudio = new Audio();
  return sfxAudio;
}

function notify(speaking: boolean, current = '') {
  listeners.forEach((l) => l({ speaking, current }));
  renderStatusBar(speaking, current);
}

function renderStatusBar(speaking: boolean, current: string) {
  if (typeof document === 'undefined') return;
  if (!statusEl) {
    statusEl = document.createElement('div');
    statusEl.id = 'speech-status-bar';
    statusEl.setAttribute('role', 'status');
    statusEl.style.cssText = `position:fixed;left:50%;transform:translateX(-50%);bottom:5.2rem;z-index:9999;
      background:linear-gradient(135deg,#032019,#0a3f30);color:#d4af37;border:1px solid #d4af37;
      border-radius:999px;padding:.55rem 1.4rem;font-size:.85rem;box-shadow:0 8px 30px rgba(0,0,0,.5);
      pointer-events:none;transition:opacity .3s;opacity:0;max-width:88vw;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;direction:ltr`;
    document.body.appendChild(statusEl);
  }
  if (speaking) {
    statusEl.textContent = `🔊 ${current || 'Lecture en cours…'}`;
    statusEl.style.opacity = '1';
  } else {
    statusEl.style.opacity = '0';
  }
}

function buildTTS(text: string, lang: string, rate: number): string {
  const speed = rate < 0.7 ? 0.4 : 0.7;
  return `https://translate.googleapis.com/translate_tts?ie=UTF-8&q=${encodeURIComponent(text)}&tl=${lang}&client=gtx&ttsspeed=${speed}`;
}

/** Fallback Web Speech API */
function webSpeak(text: string, lang: string, rate: number, onEnd: () => void) {
  try {
    const synth = window.speechSynthesis;
    if (!synth) { onEnd(); return; }
    synth.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = lang;
    u.rate = rate;
    u.volume = 1.0;
    const voices = synth.getVoices();
    if (lang.startsWith('ar')) {
      const arVoice = voices.find((v) => v.lang === 'ar-SA') || voices.find((v) => v.lang === 'ar-EG') || voices.find((v) => v.lang.startsWith('ar'));
      if (arVoice) u.voice = arVoice;
    } else {
      const v = voices.find((vo) => vo.lang === lang);
      if (v) u.voice = v;
    }
    u.onend = onEnd;
    u.onerror = onEnd;
    synth.speak(u);
  } catch {
    onEnd();
  }
}

async function playNext() {
  if (stopped) { queue = []; processing = false; notify(false); return; }
  const item = queue.shift();
  if (!item) { processing = false; notify(false); return; }

  const lang = LANG_MAP[item.lang || 'ar'];
  const rate = item.rate ?? (item.lang === 'ar' ? 0.65 : 1);
  const label = item.text.length > 42 ? item.text.slice(0, 42) + '…' : item.text;
  notify(true, label);

  await new Promise<void>((resolve) => {
    let settled = false;
    const done = () => { if (!settled) { settled = true; resolve(); } };

    // Tentative Google TTS (primaire) — timeout 5 s puis fallback Web Speech
    const audio = getAudio();
    let fallbackTimer: ReturnType<typeof setTimeout>;
    try {
      audio.onerror = () => {
        clearTimeout(fallbackTimer);
        webSpeak(item.text, lang, rate, done);
      };
      audio.onended = () => { clearTimeout(fallbackTimer); done(); };
      audio.src = buildTTS(item.text, lang, rate);
      audio.play().then(() => {
        fallbackTimer = setTimeout(() => {
          // si la lecture n'a pas démarré (réseau bloqué), fallback
          if (!settled && audio.paused) { audio.pause(); webSpeak(item.text, lang, rate, done); }
        }, 5000);
      }).catch(() => {
        clearTimeout(fallbackTimer);
        webSpeak(item.text, lang, rate, done);
      });
    } catch {
      webSpeak(item.text, lang, rate, done);
    }
  });

  if (item.pause) await new Promise((r) => setTimeout(r, item.pause));
  playNext();
}

/** File d'attente principale : coupe tout, puis enchaîne les items */
export function speakQueue(items: SpeakItem[]) {
  stopSpeech();
  stopped = false;
  queue = items.map((i) => ({ ...i, resolved: false }));
  processing = true;
  playNext();
}

/** Parle un texte simple (langue arabe par défaut) */
export function speakText(text: string, lang: 'ar' | 'fr' | 'en' = 'ar', rate?: number) {
  speakQueue([{ text, lang, rate }]);
}

/** Séquence « écouter & répéter » : texte ×2 avec pause */
export function speakRepeat(text: string, times = 2, lang: 'ar' | 'fr' | 'en' = 'ar', pause = 900) {
  const items: SpeakItem[] = [];
  for (let i = 0; i < times; i++) items.push({ text, lang, pause });
  speakQueue(items);
}

/** Carte alphabet parlante : nom phonétique → lettre (comme le site de référence) */
export function speakLetter(name: string, letter: string) {
  speakQueue([
    { text: name, lang: 'ar', rate: 0.8, pause: 700 },
    { text: letter, lang: 'ar', rate: 0.5, pause: 500 },
    { text: letter, lang: 'ar', rate: 0.5 },
  ]);
}

export function stopSpeech() {
  stopped = true;
  queue = [];
  try {
    if (sfxAudio) { sfxAudio.pause(); sfxAudio.currentTime = 0; }
    if (typeof window !== 'undefined' && window.speechSynthesis) window.speechSynthesis.cancel();
  } catch { /* ignore */ }
  notify(false);
}

/** S'abonner à l'état du moteur vocal (pour l'UI) */
export function onSpeechState(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
